import installEn from '../../../../docs/INSTALL.md?raw';
import installTr from '../../../../docs/INSTALL.tr.md?raw';
import deployEn from '../../../../docs/DEPLOY.md?raw';
import deployTr from '../../../../docs/DEPLOY.tr.md?raw';
import type { Lang } from './landingCopy.js';

/**
 * The documents the public docs section serves.
 *
 * Bodies are the repository's own Markdown, imported at build time. One source
 * of truth: a guide maintained for the engineer doing the install and a
 * separate marketing copy of it would disagree within a month, and the
 * published one would be the stale one.
 *
 * Each document exists in both languages. A Turkish reader landing on an
 * English page is the same failure as the reverse, which is why the body is
 * keyed by language rather than carrying one language and an apology.
 */
export interface Doc {
  slug: string;
  title: Record<Lang, string>;
  /** One line under the title in the index. */
  summary: Record<Lang, string>;
  body: Record<Lang, string>;
  /** Offered as a download, so a platform team can take it into their wiki. */
  rawPath: Record<Lang, string>;
  /** Roughly how long it takes to read, shown in the index. */
  minutes: number;
}

export const DOCS: Doc[] = [
  {
    slug: 'installation',
    title: { en: 'Installation guide', tr: 'Kurulum kılavuzu' },
    summary: {
      en: 'Managed cloud, public self-host, private cloud and air-gapped. Covers sizing, access, registries, identity, audit logging and backups.',
      tr: 'Yönetilen bulut, kendi sunucunuz, özel bulut ve tamamen kapalı ortam. Boyutlandırma, erişimler, imaj kayıtları, kimlik, denetim kaydı ve yedeklemeyi kapsar.',
    },
    body: { en: installEn, tr: installTr },
    rawPath: {
      en: '/downloads/replayops-installation.md',
      tr: '/downloads/replayops-kurulum.md',
    },
    minutes: 25,
  },
  {
    slug: 'deployment',
    title: { en: 'Platform deployment', tr: 'Platform dağıtımı' },
    summary: {
      en: 'Docker Compose, Kubernetes, OpenShift, Terraform and CloudFormation: the same six containers on every platform.',
      tr: 'Docker Compose, Kubernetes, OpenShift, Terraform ve CloudFormation: her platformda aynı altı konteyner.',
    },
    body: { en: deployEn, tr: deployTr },
    rawPath: {
      en: '/downloads/replayops-deployment.md',
      tr: '/downloads/replayops-dagitim.md',
    },
    minutes: 15,
  },
];

export function findDoc(slug: string | undefined): Doc | undefined {
  return DOCS.find((d) => d.slug === slug);
}

export interface DocsCopy {
  eyebrow: string;
  title: string;
  subtitle: string;
  backToSite: string;
  onThisPage: string;
  documents: string;
  readingTime: (m: number) => string;
  download: string;
  getStarted: string;
  needHelp: string;
  needHelpBody: string;
  contact: string;
  /** Accessible name for the EN | TR switch. */
  language: string;
  wizardTitle: string;
  wizardBody: string;
}

export const DOCS_COPY: Record<Lang, DocsCopy> = {
  en: {
    eyebrow: 'Documentation',
    title: 'Everything your platform team needs',
    subtitle:
      'The same guides our own engineers install from. Written for the person at the keyboard, including the parts that are not supported yet.',
    backToSite: 'Back to site',
    onThisPage: 'On this page',
    documents: 'Documents',
    readingTime: (m) => `${m} min read`,
    download: 'Download Markdown',
    getStarted: 'Get started',
    needHelp: 'Installing in a regulated or air-gapped environment?',
    needHelpBody:
      'Tell us the registry, network egress, identity provider and retention you have to work with, and we will come back with the exact configuration rather than a generic answer.',
    contact: 'Talk to us',
    language: 'Language',
    wizardTitle: 'Plan your install in four questions',
    wizardBody:
      'Answer where it runs, on what, how it is provisioned and how much traffic. Out come the commands, a starting configuration file and the machine to ask for.',
  },
  tr: {
    eyebrow: 'Dokümantasyon',
    title: 'Platform ekibinizin ihtiyacı olan her şey',
    subtitle:
      'Kendi mühendislerimizin kurulum yaparken kullandığı kılavuzların aynısı. Klavyenin başındaki kişi için yazıldı, henüz desteklenmeyen kısımlar dâhil.',
    backToSite: 'Siteye dön',
    onThisPage: 'Bu sayfada',
    documents: 'Dokümanlar',
    readingTime: (m) => `${m} dk okuma`,
    download: "Markdown'ı indir",
    getStarted: 'Hemen başla',
    needHelp: 'Regülasyonlu ya da dışarıya kapalı bir ortama mı kuracaksınız?',
    needHelpBody:
      'İmaj kaydı, dış ağ erişimi, kimlik sağlayıcı ve saklama süresi gibi kısıtlarınızı yazın. Size genel bir cevap değil, tam yapılandırmayla dönelim.',
    contact: 'Bize ulaşın',
    language: 'Dil',
    wizardTitle: 'Kurulumunuzu dört soruda planlayın',
    wizardBody:
      'Nerede, ne üzerinde, nasıl kurulacağını ve ne kadar trafik olduğunu söyleyin. Karşılığında çalıştıracağınız komutları, başlangıç yapılandırmasını ve isteyeceğiniz makineyi alın.',
  },
};
